/* Admin · Answering model — which provider and model produce every answer.
 *
 * Provider and model used to live in .env, read once at process start. Changing either meant
 * editing a file on the server and restarting the API: a deployment action, owned by whoever
 * holds the host, for what is really a product decision about answer quality. This screen
 * moves that decision here, where it is audited like every other admin action (KA-13).
 *
 * WHAT THIS SCREEN HAS TO PREVENT, LEARNED THE HARD WAY
 *
 * The first version listed the provider's models in a bare <datalist> — 67 undifferentiated
 * strings. An admin picked `gpt-5-mini` from it, which is a REASONING model, and the
 * assistant first returned "temporarily unavailable" on every question (the token budget was
 * sized for a chat model and was consumed entirely by reasoning), then, once that was fixed,
 * took 124 seconds per answer instead of 9. Both were configuration consequences of one click,
 * and nothing in the UI hinted that the click carried them.
 *
 * So the model list is not a list of strings. It groups models by what they will do to this
 * product, marks the reasoning families as slower, sinks dated snapshots and legacy versions
 * below the models anyone should actually pick, and warns in place once a reasoning model is
 * selected. The information was always available — `is_reasoning_model` has existed in
 * services/llm.py throughout — it simply was not in front of the person deciding.
 *
 * THREE OTHER DELIBERATE CHOICES
 *
 * 1. Providers that cannot be selected are shown, greyed, naming the exact environment
 *    variables that would enable them. Hiding an option makes "why can't I pick Azure"
 *    unanswerable from the screen.
 * 2. Test runs the pair WITHOUT saving it. A wrong model should be found by an admin looking
 *    at the result, not by the next employee who asks a question.
 * 3. API keys are entered HERE and nowhere else. There is no .env fallback any more: a key is
 *    typed into this screen, verified against the vendor, and stored encrypted, or the
 *    provider cannot be used. Write-only — the screen sees four characters and never the key.
 *    That makes the keys card load-bearing rather than incidental, which is why it sits above
 *    the model catalogue: without a key there is no catalogue to browse.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { llmSettings } from '../../api/client'
import type {
  LlmProviderOption,
  LlmSettings,
  LlmTestResult,
  PipelineModels,
} from '../../api/types'
import { Card, PageHead, Skeleton, dateHuman, errorMessage, useToast } from '../../ui/chrome'
import { Icon } from '../../ui/icons'

/* Mirrors REASONING_MODEL_PREFIXES in services/llm.py. Duplicated rather than fetched because
 * it decides only how a row is LABELLED here; the server remains the authority on how a model
 * is actually called, and a disagreement costs a missing badge, not a broken request. */
const REASONING_PREFIXES = ['o1', 'o3', 'o4', 'gpt-5']
/* A pinned build like gpt-5-mini-2025-08-07. Valid to select — you may want to pin one — but
 * it should never sit above its own floating alias in a list someone is scanning. */
const SNAPSHOT = /-\d{4}-\d{2}-\d{2}$/
/* Superseded families. Selectable, but they belong at the bottom. */
const LEGACY_PREFIXES = ['gpt-3.5', 'gpt-4-', 'gpt-4o-2024-05', 'gemini-1', 'gemini-2.0']

type Group = 'recommended' | 'reasoning' | 'standard' | 'snapshot' | 'legacy'

interface ModelRow {
  id: string
  group: Group
  reasoning: boolean
  preview: boolean
}

/* Deliberately small. The point is not to rank models, it is to give someone opening this
 * screen for the first time a defensible default instead of 67 equal-looking options. */
const RECOMMENDED: Record<string, string[]> = {
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-5-mini'],
  gemini: ['gemini-2.5-flash', 'gemini-2.5-pro'],
  azure: [],
}

const GROUP_META: Record<Group, { label: string; hint?: string }> = {
  recommended: { label: 'Recommended', hint: 'Sensible defaults for grounded answering' },
  standard: { label: 'Current models' },
  reasoning: {
    label: 'Reasoning models',
    hint: 'Think before answering — slower, and billed for the thinking',
  },
  snapshot: { label: 'Pinned versions', hint: 'A fixed build that will not change under you' },
  legacy: { label: 'Older generations' },
}
const GROUP_ORDER: Group[] = ['recommended', 'standard', 'reasoning', 'snapshot', 'legacy']

function isReasoning(id: string): boolean {
  const lowered = id.toLowerCase()
  return REASONING_PREFIXES.some((p) => lowered.startsWith(p))
}

function classify(id: string, provider: string): ModelRow {
  const reasoning = isReasoning(id)
  const preview = /preview|latest/.test(id)
  let group: Group = 'standard'
  if ((RECOMMENDED[provider] ?? []).includes(id)) group = 'recommended'
  else if (LEGACY_PREFIXES.some((p) => id.startsWith(p))) group = 'legacy'
  else if (SNAPSHOT.test(id)) group = 'snapshot'
  else if (reasoning) group = 'reasoning'
  return { id, group, reasoning, preview }
}

export default function ModelScreen() {
  const toast = useToast()
  const [data, setData] = useState<LlmSettings | null>(null)
  const [loading, setLoading] = useState(true)
  /* Draft state, separate from `data`: the form is an unsaved proposal until Save succeeds.
   * Editing `data` directly would make the "answering now" panel follow the controls, and the
   * screen would lose the ability to say what is actually serving questions this second. */
  const [provider, setProvider] = useState('')
  const [model, setModel] = useState('')
  const [busy, setBusy] = useState<'save' | 'test' | 'reset' | null>(null)
  const [testResult, setTestResult] = useState<LlmTestResult | null>(null)
  const [models, setModels] = useState<string[]>([])
  const [modelsLive, setModelsLive] = useState(true)
  const [modelsBusy, setModelsBusy] = useState(false)
  const [filter, setFilter] = useState('')
  const [showKeys, setShowKeys] = useState(false)
  const [keyDraft, setKeyDraft] = useState<Record<string, string>>({})
  const [keyBusy, setKeyBusy] = useState<string | null>(null)
  /* The other two model decisions. Loaded separately from the answering model because they
   * carry different state — the embedding one has to report what the index was actually built
   * with, which no amount of "current setting" can tell you. */
  const [pipeline, setPipeline] = useState<PipelineModels | null>(null)
  const [ocrDraft, setOcrDraft] = useState('')
  const [embDraft, setEmbDraft] = useState('')
  const [pipeBusy, setPipeBusy] = useState<'ocr' | 'embedding' | null>(null)
  const [confirmReindex, setConfirmReindex] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const next = await llmSettings.get()
      setData(next)
      setProvider(next.provider)
      setModel(next.model)
    } catch (err) {
      toast('err', 'Could not load model settings', errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    void load()
  }, [load])

  const loadModels = useCallback(async (which: string) => {
    if (!which) return
    setModelsBusy(true)
    try {
      const list = await llmSettings.models(which)
      setModels(list.models)
      setModelsLive(list.live)
    } catch {
      // A failed list is not a failed screen — the name can still be typed.
      setModels([])
      setModelsLive(false)
    } finally {
      setModelsBusy(false)
    }
  }, [])

  useEffect(() => {
    void loadModels(provider)
  }, [provider, loadModels])

  const loadPipeline = useCallback(async () => {
    try {
      const next = await llmSettings.pipeline()
      setPipeline(next)
      setOcrDraft(next.ocr.model)
      setEmbDraft(next.embedding.model)
    } catch (err) {
      toast('err', 'Could not load OCR and embedding settings', errorMessage(err))
    }
  }, [toast])

  useEffect(() => {
    void loadPipeline()
  }, [loadPipeline])

  async function savePipeline(which: 'ocr' | 'embedding') {
    setPipeBusy(which)
    try {
      const next = await llmSettings.savePipeline(
        which === 'ocr'
          ? { ocr_model: ocrDraft.trim() }
          : { embedding_model: embDraft.trim(), confirm_reindex: confirmReindex },
      )
      setPipeline(next)
      setOcrDraft(next.ocr.model)
      setEmbDraft(next.embedding.model)
      setConfirmReindex(false)
      toast(
        'ok',
        which === 'ocr' ? 'OCR model updated' : 'Embedding model updated',
        which === 'ocr'
          ? 'Applies to documents ingested from now on.'
          : 'Re-embed the corpus before trusting retrieval again.',
      )
    } catch (err) {
      toast('err', 'Could not save', errorMessage(err))
    } finally {
      setPipeBusy(null)
    }
  }

  const option = data?.options.find((o) => o.id === provider)
  const dirty = !!data && (provider !== data.provider || model.trim() !== data.model)
  const canSubmit = !!option?.available && model.trim().length > 0 && !busy

  const grouped = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    const rows = models
      .map((id) => classify(id, provider))
      .filter((r) => !needle || r.id.toLowerCase().includes(needle))
    const out: { group: Group; rows: ModelRow[] }[] = []
    for (const group of GROUP_ORDER) {
      const inGroup = rows.filter((r) => r.group === group)
      if (inGroup.length) out.push({ group, rows: inGroup })
    }
    return out
  }, [models, provider, filter])

  const selectedIsReasoning = isReasoning(model)
  const knownModel = models.includes(model.trim())

  // Selecting a provider here means "configure this one", NOT "save this one". An unavailable
  // provider MUST be selectable: its key card only renders for the selection, so refusing the
  // selection made a provider with no key permanently unreachable — no key, so not selectable;
  // not selectable, so nowhere to paste the key. Save stays gated on `option.available`
  // (canSubmit) and Test on the same, so nothing can be put in force without a verified key.
  function pickProvider(next: LlmProviderOption) {
    setProvider(next.id)
    // Carrying the old model name across providers is always wrong — a Gemini model name is
    // not an OpenAI one — so fall back to this provider's first recommendation.
    // Fall through to whatever the provider itself offers before giving up on a value. Azure
    // has no RECOMMENDED entry — its "models" list is the configured deployment — so keying on
    // RECOMMENDED alone emptied the field and disabled Save until the admin retyped a
    // deployment name from memory.
    setModel(RECOMMENDED[next.id]?.[0] ?? next.models[0] ?? '')
    setTestResult(null)
    setFilter('')
  }

  function pickModel(id: string) {
    setModel(id)
    setTestResult(null)
  }

  async function runTest() {
    if (!option?.available) return
    setBusy('test')
    setTestResult(null)
    try {
      const result = await llmSettings.test({ provider: provider as never, model: model.trim() })
      setTestResult(result)
      if (!result.ok) toast('err', 'That model did not respond', result.detail)
    } catch (err) {
      toast('err', 'Test failed', errorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  async function save() {
    setBusy('save')
    try {
      const next = await llmSettings.save({ provider: provider as never, model: model.trim() })
      setData(next)
      setProvider(next.provider)
      setModel(next.model)
      setTestResult(null)
      toast('ok', 'Answering model updated', `Every new answer now comes from ${next.model}.`)
    } catch (err) {
      toast('err', 'Could not save', errorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  async function reset() {
    setBusy('reset')
    try {
      const next = await llmSettings.reset()
      setData(next)
      setProvider(next.provider)
      setModel(next.model)
      setTestResult(null)
      toast('ok', 'Reset to server configuration', `Now following ${next.model} from the environment.`)
    } catch (err) {
      toast('err', 'Could not reset', errorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  async function saveKey(providerId: string) {
    const value = (keyDraft[providerId] ?? '').trim()
    if (!value) return
    setKeyBusy(providerId)
    try {
      const next = await llmSettings.setKey({ provider: providerId as never, api_key: value })
      setData(next)
      // Cleared the moment it is accepted: a key left in an input is a key in the DOM and in
      // any screenshot taken of this page.
      setKeyDraft((d) => ({ ...d, [providerId]: '' }))
      toast('ok', 'Key saved', 'Verified with the provider and stored encrypted.')
      void loadModels(provider)
    } catch (err) {
      toast('err', 'Key not accepted', errorMessage(err))
    } finally {
      setKeyBusy(null)
    }
  }

  async function removeKey(providerId: string) {
    setKeyBusy(providerId)
    try {
      const next = await llmSettings.clearKey(providerId)
      setData(next)
      // NOT "the server configuration applies again" — the .env fallback is gone, so removing
      // the key of the provider in use takes the assistant offline until another is entered.
      toast(
        'ok',
        'Key removed',
        `${option?.label ?? 'This provider'} cannot answer until a new key is added.`,
      )
    } catch (err) {
      toast('err', 'Could not remove key', errorMessage(err))
    } finally {
      setKeyBusy(null)
    }
  }

  if (loading) return <Skeleton />

  return (
    <>
      <PageHead
        title="Answering model"
        sub="The provider and model behind every answer. Changes take effect on the next question — no restart."
      />

      {/* ---------------------------------------------------------------- status */}
      {data && (
        <div className="ms-hero">
          <div className="ms-hero-main">
            <span className="ms-hero-eyebrow">Answering now</span>
            <div className="ms-hero-model">
              <span className="ms-hero-name">{data.model}</span>
              <span className="ms-tag prov">{providerLabel(data, data.provider)}</span>
              {isReasoning(data.model) && (
                <span className="ms-tag think">
                  <Icon name="brain" size={12} /> Reasoning
                </span>
              )}
            </div>
            <p className="ms-hero-sub">
              {data.source === 'override' ? (
                <>
                  Chosen by {data.updated_by_upn ?? 'an admin'}
                  {data.updated_at ? ` · ${dateHuman(data.updated_at)}` : ''}
                </>
              ) : (
                'Default — not yet chosen by an admin'
              )}
            </p>
          </div>
          {data.source === 'override' && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void reset()}
              disabled={!!busy}
            >
              {busy === 'reset' ? 'Resetting…' : 'Reset to server default'}
            </button>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------------- provider */}
      <Card title="Provider">
        <div className="ms-providers">
          {data?.options.map((o) => (
            <button
              key={o.id}
              type="button"
              className={`ms-prov-card${provider === o.id ? ' on' : ''}${o.available ? '' : ' off'}`}
              onClick={() => pickProvider(o)}
              aria-pressed={provider === o.id}
            >
              <span className="ms-prov-top">
                <span className="ms-prov-name">{o.label}</span>
                {provider === o.id && <Icon name="check" size={15} />}
              </span>
              <KeyState option={o} />
            </button>
          ))}
        </div>
      </Card>

      {/* ---------------------------------------------------------------- key */}
      {/* The SELECTED provider's key, and only that one. Listing all three put two keys on
          screen that nothing on this page was going to use, and invited an admin to rotate a
          credential for a provider they had not chosen — a change with no visible effect and
          no obvious way to tell it had none. Pick the provider first; its key follows. */}
      {option && (
        <Card
          title={`${option.label} API key`}
          actions={
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowKeys((v) => !v)}
              aria-expanded={showKeys}
            >
              {showKeys ? 'Hide' : option.key_source === 'stored' ? 'Replace' : 'Add key'}
            </button>
          }
        >
          {!showKeys ? (
            <div className="ms-key-sum single">
              <KeyState option={option} />
              {option.key_source === 'stored' && option.key_updated_by_upn && (
                <span className="ms-key-by">
                  {option.key_updated_by_upn}
                  {option.key_updated_at ? ` · ${dateHuman(option.key_updated_at)}` : ''}
                </span>
              )}
            </div>
          ) : (
            <div className="ms-key">
              <div className="ms-key-row">
                <input
                  className="inp"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={
                    option.key_source === 'none'
                      ? `Paste a ${option.label} API key`
                      : 'Paste a new key to replace the current one'
                  }
                  value={keyDraft[option.id] ?? ''}
                  onChange={(e) => setKeyDraft((d) => ({ ...d, [option.id]: e.target.value }))}
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => void saveKey(option.id)}
                  disabled={!(keyDraft[option.id] ?? '').trim() || keyBusy === option.id}
                >
                  {keyBusy === option.id ? 'Checking…' : 'Save'}
                </button>
                {option.key_source === 'stored' && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => void removeKey(option.id)}
                    disabled={keyBusy === option.id}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          )}
        </Card>
      )}
      {/* ---------------------------------------------------------------- model */}
      <Card
        title={option?.model_label ?? 'Model'}
        hint={
          modelsBusy
            ? 'Reading the list from the provider…'
            : modelsLive && models.length
              ? `${models.length} available`
              : 'List unavailable — type a name'
        }
        actions={
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void loadModels(provider)}
            disabled={modelsBusy || !option?.available}
          >
            <Icon name="refresh" size={14} /> Refresh
          </button>
        }
      >
        <div className="ms-picked">
          <div className="ms-picked-l">
            <span className="ms-picked-lbl">Selected</span>
            <input
              className="inp ms-picked-inp"
              value={model}
              onChange={(e) => {
                setModel(e.target.value)
                setTestResult(null)
              }}
              placeholder={RECOMMENDED[provider]?.[0] ?? 'model name'}
              spellCheck={false}
              disabled={!option?.available}
              aria-label="Selected model"
            />
          </div>
          <div className="ms-picked-r">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void runTest()}
              disabled={!canSubmit}
            >
              {busy === 'test' ? 'Testing…' : 'Test'}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => void save()}
              disabled={!canSubmit || !dirty}
            >
              {busy === 'save' ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {/* The warning that would have prevented both incidents. In place, at the moment of
            choosing, rather than in a doc nobody reads before clicking. */}
        {selectedIsReasoning && (
          <div className="ms-note think">
            <Icon name="brain" size={16} />
            <div>
              <b>This is a reasoning model.</b> It thinks before answering, which is slower and
              is billed for the thinking. Effort is capped at <code>minimal</code> on the server
              to keep answers near three seconds — raise{' '}
              <code>OPENAI_REASONING_EFFORT</code> if you want more deliberation and can accept
              the wait.
            </div>
          </div>
        )}
        {!knownModel && model.trim() && modelsLive && models.length > 0 && (
          <div className="ms-note warn">
            <Icon name="warn" size={16} />
            <div>
              <b>{model.trim()}</b> is not in the provider&rsquo;s list. That is allowed — a model
              released today will not be listed yet — but test it before saving.
            </div>
          </div>
        )}

        {testResult && (
          <div className={`ms-note ${testResult.ok ? 'ok' : 'err'}`}>
            <Icon name={testResult.ok ? 'checkCircle' : 'warn'} size={16} />
            <div>
              <b>
                {testResult.ok
                  ? `${testResult.model} responded in ${testResult.latency_ms} ms`
                  : `${testResult.model} did not respond`}
              </b>
              <div className="ms-note-d">{testResult.detail}</div>
            </div>
          </div>
        )}

        {dirty && (
          <div className="ms-note pending">
            <Icon name="clock" size={16} />
            <div>
              Not saved yet — questions are still answered by <b>{data?.model}</b>.
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------ browse */}
        {models.length > 0 && (
          <div className="ms-browse">
            <div className="ms-search">
              <Icon name="search" size={15} />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={`Search ${models.length} models…`}
                spellCheck={false}
                aria-label="Filter models"
              />
              {filter && (
                <button type="button" className="ms-search-x" onClick={() => setFilter('')}>
                  <Icon name="x" size={14} />
                </button>
              )}
            </div>

            <div className="ms-list">
              {grouped.length === 0 && (
                <p className="ms-empty">Nothing matches &ldquo;{filter}&rdquo;.</p>
              )}
              {grouped.map(({ group, rows }) => (
                <div key={group} className="ms-group">
                  <div className="ms-group-h">
                    <span className="ms-group-t">{GROUP_META[group].label}</span>
                    {GROUP_META[group].hint && (
                      <span className="ms-group-d">{GROUP_META[group].hint}</span>
                    )}
                  </div>
                  {rows.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      className={`ms-row${model === row.id ? ' on' : ''}`}
                      onClick={() => pickModel(row.id)}
                    >
                      <span className="ms-row-id">{row.id}</span>
                      <span className="ms-row-tags">
                        {row.group === 'recommended' && (
                          <span className="ms-tag good">Recommended</span>
                        )}
                        {row.reasoning && <span className="ms-tag think">Slower</span>}
                        {row.preview && <span className="ms-tag muted">Preview</span>}
                      </span>
                      {model === row.id && <Icon name="check" size={15} />}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>


      {/* ------------------------------------------------- OCR & embeddings (0017) */}
      {pipeline && (
        <Card
          title="Document OCR"
          actions={<span className="ms-src-tag">{pipeline.ocr.source === 'override' ? 'Set by an admin' : 'Server default'}</span>}
        >
          <div className="ms-picked">
            <div className="ms-picked-l">
              <span className="ms-picked-lbl">Model</span>
              <select
                className="inp ms-picked-inp"
                value={ocrDraft}
                onChange={(e) => setOcrDraft(e.target.value)}
                disabled={!pipeline.ocr.live}
              >
                {!pipeline.ocr.models.includes(ocrDraft) && <option value={ocrDraft}>{ocrDraft}</option>}
                {pipeline.ocr.models.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="ms-picked-r">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => void savePipeline('ocr')}
                disabled={pipeBusy === 'ocr' || ocrDraft === pipeline.ocr.model}
              >
                {pipeBusy === 'ocr' ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
          <p className="ms-hint">Applies to documents ingested from now on.</p>
        </Card>
      )}

      {pipeline && (
        <Card
          title="Embeddings"
          actions={<span className="ms-src-tag">{pipeline.embedding.source === 'override' ? 'Set by an admin' : 'Server default'}</span>}
        >
          {/* The index state comes first, because it is the fact that decides whether changing
              this control is safe. A setting alone cannot tell you the corpus disagrees with it. */}
          <div
            className={`ms-index${pipeline.embedding.stale ? ' stale' : ''}${
              !pipeline.embedding.indexed_model && pipeline.embedding.embedded_chunks > 0
                ? ' unknown'
                : ''
            }`}
          >
            <Icon
              name={
                pipeline.embedding.stale
                  ? 'warn'
                  : !pipeline.embedding.indexed_model && pipeline.embedding.embedded_chunks > 0
                    ? 'info'
                    : 'checkCircle'
              }
              size={16}
            />
            <div>
              {/* Three states, not two. "We have not recorded what built this index" is not the
                  same as "it matches", and rendering the green branch for it asserted a check
                  that had never run. */}
              {!pipeline.embedding.indexed_model && pipeline.embedding.embedded_chunks > 0 ? (
                <>
                  <b>Index provenance not recorded.</b>{' '}
                  {pipeline.embedding.embedded_chunks.toLocaleString()} vectors exist, but which
                  model produced them was never stored — so a mismatch cannot be detected here.
                  Re-embed to establish it.
                </>
              ) : pipeline.embedding.stale ? (
                <>
                  <b>The index does not match this setting.</b> The{' '}
                  {pipeline.embedding.embedded_chunks.toLocaleString()} stored vectors were built
                  with <code>{pipeline.embedding.indexed_model}</code>, but queries are now
                  embedded with <code>{pipeline.embedding.model}</code>. Vectors from different
                  models are not comparable — retrieval will return wrong documents until the
                  corpus is re-embedded.
                </>
              ) : (
                <>
                  <b>Index is consistent.</b>{' '}
                  {pipeline.embedding.embedded_chunks.toLocaleString()} of{' '}
                  {pipeline.embedding.total_chunks.toLocaleString()} chunks embedded with{' '}
                  <code>{pipeline.embedding.indexed_model ?? pipeline.embedding.model}</code>.
                </>
              )}
            </div>
          </div>

          <div className="ms-picked">
            <div className="ms-picked-l">
              <span className="ms-picked-lbl">Model</span>
              <select
                className="inp ms-picked-inp"
                value={embDraft}
                onChange={(e) => {
                  setEmbDraft(e.target.value)
                  setConfirmReindex(false)
                }}
                disabled={!pipeline.embedding.live}
              >
                {!pipeline.embedding.models.includes(embDraft) && (
                  <option value={embDraft}>{embDraft}</option>
                )}
                {pipeline.embedding.models.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="ms-picked-r">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => void savePipeline('embedding')}
                disabled={
                  pipeBusy === 'embedding' ||
                  embDraft === pipeline.embedding.model ||
                  (pipeline.embedding.embedded_chunks > 0 && !confirmReindex)
                }
              >
                {pipeBusy === 'embedding' ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {/* Only shown once a change is actually pending. A checkbox that is always there
              becomes furniture; one that appears the moment it matters gets read. */}
          {embDraft !== pipeline.embedding.model && pipeline.embedding.embedded_chunks > 0 && (
            <label className="ms-confirm">
              <input
                type="checkbox"
                checked={confirmReindex}
                onChange={(e) => setConfirmReindex(e.target.checked)}
              />
              <span>
                I understand that {pipeline.embedding.embedded_chunks.toLocaleString()} chunks
                must be re-embedded with <code>{embDraft}</code>, and that retrieval will return
                wrong documents until that is done.
              </span>
            </label>
          )}

          <p className="ms-hint">
            Must be {pipeline.embedding.required_dimensions} dimensions — another width needs a
            database migration.
          </p>
        </Card>
      )}
    </>
  )
}

/* Whether this provider has a key at all. Two states, not three: the .env fallback was removed,
 * so a key either exists in the store or the provider cannot be used. */
function KeyState({ option }: { option: LlmProviderOption }) {
  if (option.key_source === 'stored') {
    return (
      <span className="ms-key-state ok">
        <Icon name="check" size={12} /> Key ····{option.key_hint}
      </span>
    )
  }
  return (
    <span className="ms-key-state none">
      <Icon name="warn" size={12} /> No key
    </span>
  )
}

function providerLabel(data: LlmSettings, id: string): string {
  return data.options.find((o) => o.id === id)?.label ?? id
}
